import React from 'react';
import { ShieldCheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { credentials, values } from '../data/about';
import { images } from '../data/images';
import { site } from '../data/site';
import { container } from '../utils/styles';
import { PageHeader } from '../components/shared/PageHeader';
import { SectionHeading } from '../components/shared/SectionHeading';
import { CtaBand } from '../components/shared/CtaBand';
const philosophy = [{
  title: {
    en: 'Start by listening',
    es: 'Empezar escuchando'
  },
  body: {
    en: 'Our intake is thorough on purpose. We learn your loved one’s history, habits, favorite foods, and what a good day looks like for them.',
    es: 'Nuestro proceso inicial es minucioso a propósito. Conocemos la historia, los hábitos, las comidas favoritas de tu ser querido y cómo es un buen día para él o ella.'
  }
}, {
  title: {
    en: 'Keep care consistent',
    es: 'Mantener un cuidado constante'
  },
  body: {
    en: 'The same familiar caregiver, on the schedule you asked for. Consistency builds trust, especially for someone living with dementia.',
    es: 'El mismo cuidador conocido, en el horario que pediste. La constancia crea confianza, especialmente para alguien con demencia.'
  }
}, {
  title: {
    en: 'Care for the whole family',
    es: 'Cuidar a toda la familia'
  },
  body: {
    en: 'We keep you informed, answer at any hour, and show you how to help between visits so no one carries the load alone.',
    es: 'Te mantenemos informado, respondemos a cualquier hora y te enseñamos a ayudar entre visitas para que nadie cargue solo con todo.'
  }
}];
export function About() {
  const {
    t
  } = useLanguage();
  useDocumentMeta('about');
  return <>
      <PageHeader crumb={t({
      en: 'About',
      es: 'Acerca de'
    })} title={t({
      en: 'Loving Care, Rooted in Woodbridge',
      es: 'Cuidado Amoroso, con Raíces en Woodbridge'
    })} intro={t({
      en: 'A.M. Home Care, Inc. is a bilingual home care agency helping Northern Virginia seniors live safely, comfortably, and with dignity at home.',
      es: 'A.M. Home Care, Inc. es una agencia bilingüe de cuidado en el hogar que ayuda a personas mayores del norte de Virginia a vivir en casa con seguridad, comodidad y dignidad.'
    })} data-id="element-464" data-mp="2w7f3h1pyk7x8" />

      <section className="bg-white py-20 sm:py-24" aria-labelledby="mission-heading" data-id="element-465" data-mp="27lxikhfrk9gn">
        <div className={`${container} grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-16`} data-id="element-466" data-mp="2cjpgbs33apsr">
          <div className="lg:col-span-6" data-id="element-467" data-mp="2glas1a3w0y1d">
            <img src={images.team.src} alt={t(images.team.alt)} className="h-80 w-full rounded-2xl object-cover sm:h-[28rem]" data-id="element-468" data-mp="2gmc2bcd2xgi1" />
          </div>
          <div className="lg:col-span-6" data-id="element-469" data-mp="m81d32skokbq">
            <SectionHeading id="mission-heading" title={t({
            en: 'Our Mission',
            es: 'Nuestra Misión'
          })} data-id="element-470" data-mp="m92zu4iyav9a" />
            <blockquote className="mt-6 border-l-4 border-gold pl-6 font-display text-2xl font-semibold leading-snug text-brand-dark" data-id="element-471" data-mp="r7eauikzfbt3">
              “{t(site.tagline)}.”
            </blockquote>
            <p className="mt-6 text-lg leading-relaxed text-ink-soft" data-id="element-472" data-mp="evv6uw0f1ovf">
              {t({
              en: 'We started A.M. Home Care because every family deserves a caregiver they can trust completely: someone skilled, punctual, and genuinely kind, who speaks their language and treats their parent or grandparent like their own.',
              es: 'Fundamos A.M. Home Care porque toda familia merece un cuidador en quien pueda confiar plenamente: alguien capacitado, puntual y genuinamente amable, que hable su idioma y trate a su padre, madre o abuelo como si fuera suyo.'
            })}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-ink-soft" data-id="element-473" data-mp="om6mhftgpmi5">
              {t({
              en: 'Today our team serves families across Prince William, Fairfax, Arlington, Loudoun, and Alexandria, around the clock.',
              es: 'Hoy nuestro equipo sirve a familias en Prince William, Fairfax, Arlington, Loudoun y Alexandria, a toda hora.'
            })}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-cream py-20 sm:py-24" aria-labelledby="values-heading" data-id="element-474" data-mp="r62vgdlgmzft">
        <div className={container} data-id="element-475" data-mp="w4w2bn68rs70">
          <SectionHeading id="values-heading" title={t({
          en: 'Our Values',
          es: 'Nuestros Valores'
        })} data-id="element-476" data-mp="w5xpur4g275m" />
          <ul className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-477" data-mp="1jvi6h3d4ksbq">
            {values.map(v => {
            const Icon = v.icon;
            return <li key={v.id} className="border-t-2 border-brand pt-6" data-id="element-478" data-mp="1auk38naigou7">
                  <Icon className="h-7 w-7 text-brand" aria-hidden="true" data-id="element-479" data-mp="1evdp7b4e7tkg" />
                  <h3 className="mt-4 text-xl font-bold text-ink" data-id="element-480" data-mp="1exgs2vqn9h6l">{t(v.title)}</h3>
                  <p className="mt-2 leading-relaxed text-ink-soft" data-id="element-481" data-mp="3viganiss1mdu">{t(v.body)}</p>
                </li>;
          })}
          </ul>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-24" aria-labelledby="team-heading" data-id="element-482" data-mp="3s12adg8g15d4">
        <div className={`${container} grid gap-12 lg:grid-cols-12 lg:gap-16`} data-id="element-483" data-mp="3n3ilzni17c7g">
          <div className="lg:col-span-5" data-id="element-484" data-mp="5uvjksdr033k">
            <SectionHeading id="team-heading" title={t({
            en: 'Our Team & Experience',
            es: 'Nuestro Equipo y Experiencia'
          })} intro={t({
            en: 'With more than 10 years of experience and 500+ families served, our caregivers bring deep hands-on knowledge of home care, memory care, and end-of-life support. Many are bilingual, and every one is carefully screened, trained, and supervised.',
            es: 'Con más de 10 años de experiencia y más de 500 familias atendidas, nuestros cuidadores tienen un profundo conocimiento práctico del cuidado en el hogar, el cuidado de la memoria y el apoyo al final de la vida. Muchos son bilingües, y cada uno es cuidadosamente seleccionado, capacitado y supervisado.'
          })} data-id="element-485" data-mp="5tkjb2jihdh7" />
            <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-8" data-id="element-486" data-mp="3b9dt1npa48a3">
              <div data-id="element-487" data-mp="3fb8ssnrkadlr">
                <dt className="text-ink-soft" data-id="element-488" data-mp="3fapc1adndv9b">{t({
                  en: 'Families served',
                  es: 'Familias atendidas'
                })}</dt>
                <dd className="font-display text-4xl font-bold text-brand-dark" data-id="element-489" data-mp="3k3s7jo1zjes5">500+</dd>
              </div>
              <div data-id="element-490" data-mp="3p124dorpesq0">
                <dt className="text-ink-soft" data-id="element-491" data-mp="3p3oadjyy5064">{t({
                  en: 'Years of experience',
                  es: 'Años de experiencia'
                })}</dt>
                <dd className="font-display text-4xl font-bold text-brand-dark" data-id="element-492" data-mp="2sdnr20hcjbw7">10+</dd>
              </div>
              <div data-id="element-493" data-mp="31bzexuqtvvky">
                <dt className="text-ink-soft" data-id="element-494" data-mp="31bg5z3j5ohj2">{t({
                  en: 'Care available',
                  es: 'Cuidado disponible'
                })}</dt>
                <dd className="font-display text-2xl font-bold text-ink" data-id="element-495" data-mp="36bcb563laf07">24/7</dd>
              </div>
              <div data-id="element-496" data-mp="gmjpb33cicww">
                <dt className="text-ink-soft" data-id="element-497" data-mp="gkywpj0ld41o">{t({
                  en: 'Google rating',
                  es: 'Calificación en Google'
                })}</dt>
                <dd className="font-display text-2xl font-bold text-ink" data-id="element-498" data-mp="lja6jtqvzejw">5.0★</dd>
              </div>
            </dl>
          </div>
          <div className="lg:col-span-7" data-id="element-499" data-mp="213sxssbqlihe">
            <h2 className="text-3xl font-bold text-ink sm:text-4xl" data-id="element-500" data-mp="214ub73g86h2a">{t({
              en: 'Our Care Philosophy',
              es: 'Nuestra Filosofía de Cuidado'
            })}</h2>
            <div className="mt-8 divide-y divide-mist border-t border-mist" data-id="element-501" data-mp="1wazw6dbkeq9j">
              {philosophy.map(p => <div key={p.title.en} className="py-6" data-id="element-502" data-mp="1ncebabimez5l">
                  <h3 className="text-xl font-bold text-ink" data-id="element-503" data-mp="1ndz8k8wbkw75">{t(p.title)}</h3>
                  <p className="mt-2 text-lg leading-relaxed text-ink-soft" data-id="element-504" data-mp="1ic9uslhpq4fd">{t(p.body)}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-tint py-20 sm:py-24" aria-labelledby="credentials-heading" data-id="element-505" data-mp="18fx4zypagapw">
        <div className={`${container} grid gap-10 lg:grid-cols-12`} data-id="element-506" data-mp="37sho8fjsa1g8">
          <div className="lg:col-span-4" data-id="element-507" data-mp="32t52xz8k92fr">
            <SectionHeading id="credentials-heading" title={t({
            en: 'Credentials & Certifications',
            es: 'Credenciales y Certificaciones'
          })} intro={t({
            en: 'The safeguards behind every visit, so you can trust who walks through your door.',
            es: 'Las garantías detrás de cada visita, para que confíes en quien entra por tu puerta.'
          })} data-id="element-508" data-mp="32u7u889sp6w7" />
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 lg:col-span-8" data-id="element-509" data-mp="1sxayvbftibpy">
            {credentials.map(c => <li key={c.en} className="flex items-start gap-3 rounded-xl bg-white p-5" data-id="element-510" data-mp="1xwe1yqc8m7va">
                <ShieldCheckIcon className="mt-0.5 h-6 w-6 shrink-0 text-sage-dark" aria-hidden="true" data-id="element-511" data-mp="1jwmk2eilo7c0" />
                <span className="font-semibold text-ink" data-id="element-512" data-mp="1ny872soum1zx">{t(c)}</span>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-513" data-mp="3ebyydwgyjqj1" />
    </>;
}